import { treeUtil } from '@salescore/buff-common'
import type { SheetCustomModelFieldsFragment } from '@salescore/client-api'
import { CORE_CONSTANT, type ViewConfigSheet, type ViewConfigTreeNode } from '@salescore/core'
import type { SalescoreObjectPolicyAction } from '@salescore/features'
import { Array, Filter, pipe } from 'effect'

import type { Me } from '../hooks/useMe'

/** シートに表示するカスタムオブジェクトについて、個別権限で許可された操作 */
export interface CanSalescoreObjectAction {
  canSaveSalescoreObject: boolean
  /** 個別権限で「レコードの編集」が許可されたカスタムオブジェクトが、シートに 1 つでもあるか */
  canEditSalescoreObjectRecord: boolean
  /** 保存済みの設定の列に出ているカスタムオブジェクトのどれか 1 つに、個別権限で「レコードの削除」が許可されているか */
  canDeleteSalescoreObjectRecord: boolean
  /** 保存済みの設定の列に出ているカスタムオブジェクトのどれか 1 つに、個別権限で「レコードの新規作成」が許可されているか */
  canCreateSalescoreObjectRecord: boolean
  /**
   * シートの「レコードの保存」権限がなくても、個別権限で編集できるオブジェクトの名前
   * - 個別権限で編集を許可されたカスタムオブジェクトと、そのオブジェクトに追加したシートのカスタム列を含む
   * - 名前は列の書き込み先 (`ViewQueryField.write.streamName`) と比べる
   */
  editableModelNames: string[]
}

/**
 * シートに出うるノードを返す
 * - プリセットを切り替えると表示中のツリーが入れ替わるため、各プリセットのツリーのノードも含める
 */
const listSheetNodes = (config: ViewConfigSheet): ViewConfigTreeNode[] =>
  pipe(
    [config.tree, ...(config.presets ?? []).map((preset) => preset.tree)],
    Array.flatMap((tree) => treeUtil.flatNodes(tree)),
  )

/**
 * オブジェクト名の一覧から、カスタムオブジェクトを重複なく返す
 */
const findSalescoreObjectModels = ({
  eltModels,
  modelNames,
}: {
  eltModels: Array<{ id: string; name: string }>
  modelNames: string[]
}): Array<{ id: string; name: string }> =>
  pipe(
    modelNames,
    Array.filter((modelName) => modelName.startsWith(CORE_CONSTANT.SALESCORE_CUSTOM_MODEL_PREFIX)),
    Array.dedupe,
    Array.filterMap(
      Filter.fromPredicateOption((modelName) => Array.findFirst(eltModels, (model) => model.name === modelName)),
    ),
  )

/**
 * シートのカスタム列のうち、列を追加した先のノードのオブジェクトに編集権限があるものの名前を返す
 * @param editableSalescoreObjectModelNames 個別権限で「レコードの編集」を許可されたカスタムオブジェクトの名前
 */
const listEditableSheetCustomModelNames = ({
  editableSalescoreObjectModelNames,
  sheetCustomModels,
  nodes,
}: {
  editableSalescoreObjectModelNames: string[]
  sheetCustomModels: Array<Pick<SheetCustomModelFieldsFragment, 'name' | 'nodeName'>>
  nodes: ViewConfigTreeNode[]
}): string[] =>
  pipe(
    sheetCustomModels,
    Array.filter((sheetCustomModel) =>
      nodes.some(
        (node) => node.name === sheetCustomModel.nodeName && editableSalescoreObjectModelNames.includes(node.modelName),
      ),
    ),
    Array.map((sheetCustomModel) => sheetCustomModel.name),
  )

/**
 * シートに表示するカスタムオブジェクトについて、個別権限で許可された操作を求める
 * @param eltModels 組織内のオブジェクト
 */
export const createCanSalescoreObjectAction = ({
  eltModels,
  sheetCustomModels,
  config,
  me,
}: {
  eltModels: Array<{ id: string; name: string }>
  sheetCustomModels: Array<Pick<SheetCustomModelFieldsFragment, 'name' | 'nodeName'>>
  config: ViewConfigSheet
  me: Pick<Me, 'salescoreObjectPermissionUsers' | 'salescoreObjectPermissionUserGroups'>
}): CanSalescoreObjectAction => {
  const { salescoreObjectPermissionUsers, salescoreObjectPermissionUserGroups } = me
  // ユーザー個人への付与とグループへの付与は、どちらがあっても許可する
  const permissions = [...(salescoreObjectPermissionUsers ?? []), ...(salescoreObjectPermissionUserGroups ?? [])]
  const hasPermission = (modelId: string, action: SalescoreObjectPolicyAction): boolean =>
    permissions.some((permission) => permission.eltModelId === modelId && permission.action === action)
  // 新規作成・削除はシート全体で判定するため、判定の対象は保存済みの設定の列に出ているものに留める
  const savedFieldModelNames = (config.fields ?? []).map((field) => field.property.modelName)
  const savedFieldSalescoreObjectModels = findSalescoreObjectModels({ eltModels, modelNames: savedFieldModelNames })
  const canDeleteRecord = savedFieldSalescoreObjectModels.some((model) =>
    hasPermission(model.id, 'salescore-object-delete-record'),
  )
  const canCreateRecord = savedFieldSalescoreObjectModels.some((model) =>
    hasPermission(model.id, 'salescore-object-create-record'),
  )

  // 編集はセルごとに判定するため、表示しうるノードをすべて対象にする。
  // 列 (fields) でなくノードから集めるのは、通常の列をすべて外してシートのカスタム列だけを表示しているノードがあるため。
  // シートのカスタム列の書き込み先はカスタム列のモデルで、ノードのオブジェクトではない
  const nodes = listSheetNodes(config)
  const nodeModelNames = nodes.map((node) => node.modelName)
  const nodeSalescoreObjectModels = findSalescoreObjectModels({ eltModels, modelNames: nodeModelNames })
  const editableSalescoreObjectModelNames = pipe(
    nodeSalescoreObjectModels,
    Array.filter((model) => hasPermission(model.id, 'salescore-object-edit-record')),
    Array.map((model) => model.name),
  )
  const canEditRecord = editableSalescoreObjectModelNames.length > 0
  const editableSheetCustomModelNames = listEditableSheetCustomModelNames({
    editableSalescoreObjectModelNames,
    sheetCustomModels,
    nodes,
  })

  return {
    canSaveSalescoreObject: canEditRecord || canDeleteRecord || canCreateRecord,
    canEditSalescoreObjectRecord: canEditRecord,
    canDeleteSalescoreObjectRecord: canDeleteRecord,
    canCreateSalescoreObjectRecord: canCreateRecord,
    editableModelNames: [...editableSalescoreObjectModelNames, ...editableSheetCustomModelNames],
  }
}
